import { useState } from 'react';

/**
 * A small line drawing of the food a row is about, so a list of thirty items
 * can be scanned by shape rather than read word by word.
 *
 * Drawn in the same register as the nav icons in App.jsx -- 24x24, stroke only,
 * currentColor, 1.8 wide, round caps and joins -- because an app that mixes two
 * icon languages stops reading as one app. The paths are here rather than in a
 * package: an icon set would be a dependency and a download for thirty shapes
 * that never change, and a phone in a kitchen with no signal still gets these.
 *
 * An item met through its barcode knows what it actually looks like, and that
 * photograph beats any drawing of the aisle it came from. Everything else --
 * which is most of a real pantry -- is a glyph, and the glyph is also where a
 * photograph lands when it will not load.
 */

/**
 * Every glyph, keyed by the name the tables below reach for.
 *
 * Legibility at 22px is the whole constraint: two or three strokes each, no
 * detail that closes up when the tile is the size of a thumbnail. Where a
 * category and a specific food would have drawn the same picture they share one
 * entry -- a bakery item nobody named is still a loaf, and a second, vaguer loaf
 * would only be a second loaf.
 */
const GLYPHS = {
  // ---- the twelve categories ----
  produce: (
    <>
      <path d="M12 21v-8.6" />
      <path d="M12 12.4c0-4 2.7-6.9 6.8-7.3.4 4.1-2.5 7.3-6.8 7.3Z" />
      <path d="M12 16.6c0-3.3-2.1-5.6-5.4-6-.3 3.3 2 6 5.4 6Z" />
    </>
  ),
  // Rashers rather than a chop: every rounded cut of meat small enough to fit
  // here came out an oval with a bone in it, which at 22px is a fried egg.
  meat: (
    <>
      <path d="M3.6 7.2c1.9-1.7 3.5-1.7 5.4 0s3.5 1.7 5.4 0 3.5-1.7 5.4 0v3.6c-1.9-1.7-3.5-1.7-5.4 0s-3.5 1.7-5.4 0-3.5-1.7-5.4 0Z" />
      <path d="M3.6 13.2c1.9-1.7 3.5-1.7 5.4 0s3.5 1.7 5.4 0 3.5-1.7 5.4 0v3.6c-1.9-1.7-3.5-1.7-5.4 0s-3.5 1.7-5.4 0-3.5-1.7-5.4 0Z" />
    </>
  ),
  frozen: (
    <>
      <path d="M12 3v18" />
      <path d="M4.2 7.5 19.8 16.5" />
      <path d="M19.8 7.5 4.2 16.5" />
    </>
  ),
  pantry: (
    <>
      <ellipse cx="12" cy="6.4" rx="5.8" ry="2.6" />
      <path d="M6.2 6.4v11.2c0 1.4 2.6 2.6 5.8 2.6s5.8-1.2 5.8-2.6V6.4" />
      <path d="M6.4 10.6h11.2" />
    </>
  ),
  spices: (
    <>
      <path d="M8 9.6h8v9.2a2.2 2.2 0 0 1-2.2 2.2h-3.6A2.2 2.2 0 0 1 8 18.8Z" />
      <path d="M8.6 9.6V7.4a3.4 3.4 0 0 1 6.8 0v2.2" />
      <path d="M10.6 5.6v1" />
      <path d="M12 5.2v1" />
      <path d="M13.4 5.6v1" />
    </>
  ),
  drink: (
    <>
      <path d="M6.6 6.6h10.8l-1.3 12.5a2 2 0 0 1-2 1.8h-4.2a2 2 0 0 1-2-1.8Z" />
      <path d="M14 6.6 16.6 3" />
      <path d="M7.5 11.2h9" />
    </>
  ),
  snacks: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="9.4" cy="9.8" r="0.9" />
      <circle cx="14.2" cy="10.4" r="0.9" />
      <circle cx="12.4" cy="14.6" r="0.9" />
      <circle cx="8.6" cy="14.2" r="0.8" />
    </>
  ),
  household: (
    <>
      <path d="M9 9.6h6v10.2a1.2 1.2 0 0 1-1.2 1.2h-3.6A1.2 1.2 0 0 1 9 19.8Z" />
      <path d="M10.4 9.6V7.2h3.2v2.4" />
      <path d="M13.6 7.2V5.4h3.4" />
      <path d="M18.8 3.6 20.6 3" />
      <path d="M18.8 5.6h2" />
      <path d="M18.8 7.6 20.6 8.2" />
    </>
  ),
  other: (
    <>
      <path d="M5.2 8h13.6l-1.2 11.4a1.8 1.8 0 0 1-1.8 1.6H8.2a1.8 1.8 0 0 1-1.8-1.6Z" />
      <path d="M8.8 8V6.2a3.2 3.2 0 0 1 6.4 0V8" />
    </>
  ),

  // ---- the specifics worth telling apart in a real kitchen list ----
  milk: (
    <>
      <path d="M7 9.6 12 4.4l5 5.2v9.4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Z" />
      <path d="M7 9.6h10" />
    </>
  ),
  egg: <path d="M12 3.6c3.2 0 5.7 4.7 5.7 8.6 0 4.2-2.5 7-5.7 7s-5.7-2.8-5.7-7c0-3.9 2.5-8.6 5.7-8.6Z" />,
  cheese: (
    <>
      <path d="M4 17.2V12l14-5v10.2a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 17.2Z" />
      <circle cx="8" cy="14.6" r="1.1" />
      <circle cx="13.2" cy="13.2" r="1" />
    </>
  ),
  butter: (
    <>
      <path d="M3 13.6 5.6 11H21v5.4L18.4 19H3Z" />
      <path d="M3 13.6h15.4V19" />
      <path d="M18.4 13.6 21 11" />
    </>
  ),
  yogurt: (
    <>
      <path d="M4.4 5.4h15.2v2.8H4.4Z" />
      <path d="M5.8 8.2 7.2 19.4a1.8 1.8 0 0 0 1.8 1.6h6a1.8 1.8 0 0 0 1.8-1.6l1.4-11.2" />
    </>
  ),
  bread: (
    <>
      <path d="M4.2 12.6C4.2 9.6 7.7 7.4 12 7.4s7.8 2.2 7.8 5.2v5.6a1.8 1.8 0 0 1-1.8 1.8H6a1.8 1.8 0 0 1-1.8-1.8Z" />
      <path d="M8.8 11.6v8.4" />
    </>
  ),
  apple: (
    <>
      <path d="M12 8.4c-1.1-1-2.7-1.3-4-.6-2 1-2.7 3.5-1.8 6.2.9 2.9 3 6.2 5.8 6.2s4.9-3.3 5.8-6.2c.9-2.7.2-5.2-1.8-6.2-1.3-.7-2.9-.4-4 .6Z" />
      <path d="M12 8.4V5.6" />
      <path d="M12.4 6.2c1-1.7 2.8-2.4 4.4-2.2-.1 1.8-1.4 3.2-3.2 3.4" />
    </>
  ),
  banana: (
    <>
      <path d="M4.9 7.6c-.4 6.6 4.4 11.6 11 11.6 2.1 0 3.7-.5 4.6-1.4-3.8-.3-6.7-1.6-8.8-4-2-2.3-3-4.8-3.1-7.6Z" />
      <path d="M4.9 7.6 4 5.5" />
    </>
  ),
  carrot: (
    <>
      <path d="M8.8 10.4h6.4l-2.4 8.6a1 1 0 0 1-1.9 0Z" />
      <path d="M12 10.4c-.5-2.2-2.2-3.5-4.4-3.5.2 2.2 1.9 3.6 3.9 3.5" />
      <path d="M12 10.4c.5-2.2 2.2-3.5 4.4-3.5-.2 2.2-1.9 3.6-3.9 3.5" />
    </>
  ),
  tomato: (
    <>
      <circle cx="12" cy="14.2" r="6.1" />
      <path d="M12 8.2V6.2" />
      <path d="M12 8.4c-1.1-.7-2.5-.8-3.7-.3.5 1.2 1.6 2 2.9 2.2" />
      <path d="M12 8.4c1.1-.7 2.5-.8 3.7-.3-.5 1.2-1.6 2-2.9 2.2" />
    </>
  ),
  onion: (
    <>
      <path d="M12 8.6c3.5 0 6.2 2.9 6.2 6.2 0 3.1-2.7 5.6-6.2 5.6s-6.2-2.5-6.2-5.6c0-3.3 2.7-6.2 6.2-6.2Z" />
      <path d="M12 8.6c-1.4 1.8-2.1 4-2.1 6.2s.7 4.2 2.1 5.6" />
      <path d="M12 8.6c1.4 1.8 2.1 4 2.1 6.2s-.7 4.2-2.1 5.6" />
      <path d="M12 8.6c-.1-1.7.7-2.9 2.2-3.6" />
    </>
  ),
  potato: (
    <>
      <path d="M4.6 13.9c-.6-3.5 2.3-6.6 6.4-7.1 4.1-.6 8.2 1.4 8.8 4.7.6 3.5-2.1 6.7-6.2 7.2-4.1.6-8.4-1.3-9-4.8Z" />
      <circle cx="9.4" cy="12.2" r="0.85" />
      <circle cx="14.2" cy="14.2" r="0.85" />
      <circle cx="13.4" cy="10.6" r="0.75" />
    </>
  ),
  leafy: (
    <>
      <path d="M20 4.4c-8.4 0-13.6 4.2-13.6 10 0 1.7.5 3.2 1.4 4.4 8.6-1.7 12.2-6.6 12.2-14.4Z" />
      <path d="M3.6 20.4c1.3-3.8 3.6-7 6.7-9.3" />
    </>
  ),
  chicken: (
    <>
      <circle cx="15.6" cy="8.6" r="4.7" />
      <path d="M12.3 11.9 8.8 15.4" />
      <circle cx="7.5" cy="14.1" r="1.8" />
      <circle cx="10.1" cy="16.7" r="1.8" />
    </>
  ),
  fish: (
    <>
      <path d="M16.6 12c0 3-2.9 5.4-6.4 5.4S3.8 15 3.8 12s2.9-5.4 6.4-5.4S16.6 9 16.6 12Z" />
      <path d="M16.6 12 20.5 9v6Z" />
      <circle cx="8" cy="10.6" r="0.9" />
    </>
  ),
  grain: (
    <>
      <path d="M12 21V9.4" />
      <path d="M12 12.4c-2.4 0-4-1.7-4-4.1 2.4 0 4 1.7 4 4.1Z" />
      <path d="M12 12.4c2.4 0 4-1.7 4-4.1-2.4 0-4 1.7-4 4.1Z" />
      <path d="M12 7.6c-2.4 0-4-1.7-4-4.1 2.4 0 4 1.7 4 4.1Z" />
      <path d="M12 7.6c2.4 0 4-1.7 4-4.1-2.4 0-4 1.7-4 4.1Z" />
    </>
  ),
  pasta: (
    <>
      <path d="M3.6 12.8h16.8c0 4.6-3.8 8.2-8.4 8.2s-8.4-3.6-8.4-8.2Z" />
      <path d="M7.8 12.8c0-1.4-1-2-1-3.3s1-1.9 1-3.3" />
      <path d="M12 12.8c0-1.4-1-2-1-3.3s1-1.9 1-3.3" />
      <path d="M16.2 12.8c0-1.4-1-2-1-3.3s1-1.9 1-3.3" />
    </>
  ),
  flour: (
    <>
      <path d="M6.6 19.2 8 9.2h8l1.4 10a1.6 1.6 0 0 1-1.6 1.8H8.2a1.6 1.6 0 0 1-1.6-1.8Z" />
      <path d="M8 9.2 9.6 6h4.8L16 9.2" />
      <path d="M9.6 6c1.5 1.1 3.3 1.1 4.8 0" />
    </>
  ),
  oil: (
    <>
      <path d="M10.4 3.4h3.2v3.2c0 .7.3 1.4.8 1.9l1.3 1.3c.8.8 1.3 1.9 1.3 3v6.4a1.8 1.8 0 0 1-1.8 1.8H8.8A1.8 1.8 0 0 1 7 19.2v-6.4c0-1.1.5-2.2 1.3-3l1.3-1.3c.5-.5.8-1.2.8-1.9Z" />
      <path d="M7.2 14.2h9.6" />
    </>
  ),
  coffee: (
    <>
      <path d="M4.4 9.4h11.2v5.8a4.8 4.8 0 0 1-4.8 4.8H9.2a4.8 4.8 0 0 1-4.8-4.8Z" />
      <path d="M15.6 10.8h1.6a2.6 2.6 0 0 1 0 5.2h-1.6" />
      <path d="M8.4 5.2c0 1-.9 1.4-.9 2.4" />
      <path d="M12 4.6c0 1-.9 1.4-.9 2.4" />
    </>
  ),
  jar: (
    <>
      <path d="M6.6 3.6h10.8v3H6.6Z" />
      <path d="M7.8 6.6h8.4v11.6a2.2 2.2 0 0 1-2.2 2.2h-4a2.2 2.2 0 0 1-2.2-2.2Z" />
      <path d="M7.8 11h8.4" />
      <path d="M7.8 15.6h8.4" />
    </>
  ),
};

/**
 * The floor under every row. `master_items.category` is NOT NULL and only ever
 * one of these twelve, so a row whose name says nothing we recognise still gets
 * a picture rather than a blank tile.
 */
const CATEGORY_GLYPH = {
  produce: 'produce',
  meat: 'meat',
  seafood: 'fish',
  dairy: 'milk',
  bakery: 'bread',
  frozen: 'frozen',
  pantry: 'pantry',
  spices: 'spices',
  beverages: 'drink',
  snacks: 'snacks',
  household: 'household',
  other: 'other',
};

/**
 * The name comes first, because the category is only ever the aisle: "Whole
 * Milk" and "2% Milk" are both a carton, and all their category knows is dairy.
 *
 * Keywords are matched as substrings of the cleaned name, which is what makes
 * plurals free -- "Sweet Potatoes" contains "sweet potato" -- and it is why the
 * longest match has to win. Roughly half the entries below exist for no other
 * reason: without them "buttermilk" is butter, "eggplant" is an egg,
 * "pineapple" is an apple, "coconut milk" is dairy, "watermelon" is a bottle of
 * water, "pepperoni" is a bell pepper, and "toilet paper" and "aluminum foil"
 * are both cooking oil. A wrong picture on a familiar food is worse than a
 * generic one, so each of those is spelled out rather than left to luck.
 *
 * Equal-length matches fall back to the order of this table, which is why
 * frozen sits above snacks: "mint chocolate chip ice cream" matches both at
 * nine characters and is a tub of ice cream.
 *
 * Grouped by glyph so it stays easy to extend -- a food already drawn needs a
 * word added to its line and nothing else.
 */
const KEYWORDS = {
  frozen: ['frozen', 'ice cream', 'popsicle'],
  milk: ['milk', 'buttermilk', 'cream', 'half and half', 'almond milk', 'chocolate milk'],
  egg: ['egg'],
  cheese: ['cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta'],
  butter: ['butter'],
  yogurt: ['yogurt', 'yoghurt', 'sour cream', 'cottage cheese'],
  bread: ['bread', 'bagel', 'tortilla', 'bun', 'baguette', 'croissant', 'muffin', 'pita',
    'garlic bread', 'banana bread', 'cheesecake', 'burger bun'],
  apple: ['apple'],
  banana: ['banana', 'plantain'],
  carrot: ['carrot', 'sweet potato', 'parsnip', 'yam'],
  tomato: ['tomato'],
  onion: ['onion', 'garlic', 'shallot', 'leek'],
  potato: ['potato'],
  leafy: ['lettuce', 'spinach', 'kale', 'arugula', 'romaine', 'cabbage', 'salad', 'chard',
    'collard', 'bok choy', 'basil', 'cilantro', 'parsley'],
  produce: ['broccoli', 'cucumber', 'celery', 'avocado', 'mushroom', 'pepper', 'berry', 'grape',
    'orange', 'lemon', 'lime', 'melon', 'peach', 'pear', 'green bean', 'zucchini', 'asparagus',
    'eggplant', 'pineapple', 'watermelon', 'butternut', 'sugar snap', 'veggie'],
  meat: ['beef', 'pork', 'steak', 'bacon', 'sausage', 'turkey', 'lamb', 'burger', 'pepperoni'],
  chicken: ['chicken'],
  fish: ['fish', 'salmon', 'tuna', 'shrimp', 'cod', 'tilapia', 'crab', 'lobster', 'scallop',
    'anchovy'],
  grain: ['rice', 'oats', 'oatmeal', 'quinoa', 'cereal', 'granola', 'barley', 'couscous', 'grits'],
  pasta: ['pasta', 'spaghetti', 'noodle', 'macaroni', 'penne', 'lasagna', 'ramen', 'ravioli'],
  flour: ['flour', 'sugar', 'yeast'],
  oil: ['oil', 'vinegar', 'cider vinegar', 'dressing'],
  coffee: ['coffee', 'tea', 'espresso', 'chai', 'matcha'],
  drink: ['water', 'juice', 'soda', 'seltzer', 'sparkling', 'lemonade', 'cola', 'beer', 'wine',
    'kombucha', 'apple cider', 'orange juice', 'apple juice', 'grape juice'],
  jar: ['jam', 'jelly', 'honey', 'syrup', 'salsa', 'pickle', 'ketchup', 'mustard', 'mayo', 'sauce',
    'peanut butter', 'almond butter', 'tomato sauce', 'tomato paste', 'pasta sauce', 'berry jam',
    'hot sauce', 'coconut milk'],
  pantry: ['broth', 'stock', 'soup', 'bean', 'lentil', 'chickpea', 'chicken broth', 'chicken stock',
    'baking soda'],
  spices: ['salt', 'black pepper', 'peppercorn', 'cinnamon', 'cumin', 'paprika', 'oregano', 'thyme',
    'rosemary', 'nutmeg', 'turmeric', 'cayenne', 'curry', 'allspice', 'bay leaf', 'vanilla',
    'chili powder', 'garlic powder', 'onion powder', 'garlic salt', 'seasoning', 'spice'],
  snacks: ['chip', 'cracker', 'cookie', 'pretzel', 'popcorn', 'candy', 'chocolate', 'peanut',
    'cashew', 'walnut', 'pecan', 'trail mix', 'granola bar', 'potato chip', 'tortilla chip'],
  household: ['toilet', 'foil', 'wrap', 'paper towel', 'napkin', 'detergent', 'soap', 'trash bag',
    'sponge', 'cleaner', 'bleach', 'shampoo', 'toothpaste'],
};

/**
 * Longest keyword first, so the first hit is the most specific one. Sorted once
 * at load rather than per row: a pantry of thirty items redraws this on every
 * keystroke of the search box.
 *
 * Array#sort is stable, so keywords of equal length keep the order they were
 * written in above.
 */
const KEYWORD_INDEX = Object.entries(KEYWORDS)
  .flatMap(([glyph, words]) => words.map((word) => [word, glyph]))
  .sort((a, b) => b[0].length - a[0].length);

/**
 * Punctuation becomes a space rather than nothing: "Half-and-Half" has to end
 * up as "half and half" for the keyword to reach it, and deleting the hyphens
 * would give "halfandhalf" instead. Collapsing runs of it is what lets every
 * keyword be written with single spaces.
 */
const clean = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

function glyphFor(name, category) {
  const text = clean(name);
  const hit = text && KEYWORD_INDEX.find(([word]) => text.includes(word));
  if (hit) return hit[1];
  return CATEGORY_GLYPH[category] || 'other';
}

/**
 * Decorative either way, and hidden accordingly: the item's name is in text
 * right beside it, so anything announced here would be the row read twice.
 *
 * A photograph is a nicety and is treated as a claim rather than a fact. The
 * URL points at somebody else's server, which can 404 or be unreachable, and a
 * kitchen list full of broken-image boxes would be worse than one with no
 * pictures at all -- so a load that fails falls back to the drawing.
 *
 * What failed is remembered by URL rather than as a flag, because React reuses
 * a component instance when a list is filtered or reordered: a new photograph
 * deserves its own attempt instead of inheriting the last one's bad luck.
 */
export default function FoodIcon({ name, category, imageUrl }) {
  const [failedUrl, setFailedUrl] = useState(null);

  if (imageUrl && imageUrl !== failedUrl) {
    return (
      <span className="food-icon" aria-hidden="true">
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(imageUrl)}
        />
      </span>
    );
  }

  return (
    <span className="food-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        {GLYPHS[glyphFor(name, category)]}
      </svg>
    </span>
  );
}
